import axios from "axios";

import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, CreateAxiosDefaults } from "axios";
// import type { RequestInterceptors } from "./type";
import { useUserStore } from "@/store";
import router from "@/router";
import qs from "qs";
import { message } from "ant-design-vue";
declare module "axios" {
  export interface AxiosRequestConfig {
    repeatRequest?: boolean;
    requestId?: string; // 添加请求ID
  }
}
let UserStore: any = null; //useUserStore();

// 不需要判断重复请求的接口列表
const NO_DUPLICATE_CHECK_URLS: (string | RegExp)[] = [
  // 示例：添加不需要重复请求检查的接口
  '/task/get-task-info'
];

// 检查URL是否在白名单中
function shouldSkipDuplicateCheck(url: string): boolean {
  return NO_DUPLICATE_CHECK_URLS.some(pattern => {
    if (typeof pattern === 'string') {
      return url.includes(pattern);
    } else if (pattern instanceof RegExp) {
      return pattern.test(url);
    }
    return false;
  });
}

// 队列map
const pendingMap = new Map<string, { controller: AbortController; requestId: string }>();
// 获取请求key 作为map键
function getRequestKey(config: AxiosRequestConfig) {
  return (config.method || "") + config.url + "?" + qs.stringify(config.params);
}
// 加入map队列，如果已存在则清除前一个，避免重复请求
function setPendingMap(config: AxiosRequestConfig) {
  // 检查是否需要跳过重复请求检查
  if (shouldSkipDuplicateCheck(config.url || '')) {
    return;
  }
  
  const controller = new AbortController();
  config.signal = controller.signal;
  const requestKey = getRequestKey(config);
  const requestId = config.requestId || requestKey; // 使用请求ID作为键
  if (pendingMap.has(requestId)) {
    const { controller: oldController } = pendingMap.get(requestId)!;
    oldController.abort();
    pendingMap.delete(requestId);
  } else {
    pendingMap.set(requestId, {controller, requestId});
  }
}
interface IResponse {
  code?: number;
  message?: string;
  data?: any;
  headers?: any;
}
class Request {
  // axios 实例
  private instance: AxiosInstance;
  // 拦截器对象
  // private interceptorsObj?: RequestInterceptors<AxiosResponse>;
  // 基础配置
  private defaultConfig: CreateAxiosDefaults = {
    baseURL: "/api/",
    timeout: 5 * 60 * 1000,
    repeatRequest: false, //允许重复请求 默认不允许
  };
  constructor(config: CreateAxiosDefaults) {
    this.instance = axios.create(Object.assign(this.defaultConfig, config));
    // 请求拦截器
    this.instance.interceptors.request.use(
      async (config) => {
        if (!config.repeatRequest) {
          setPendingMap(config);
        }
        if (!UserStore) {
          UserStore = useUserStore();
        }
        await updateTimeHandle();
        if (UserStore.userInfo.authorization) {
          config.headers.Authorization = "Bearer " + UserStore.userInfo.authorization;
        }
        return config;
      },
      (err: any) => err
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (res: AxiosResponse) => {
        const config = res.config;
        // 只有不在白名单中的请求才需要从pendingMap中删除
        if (!shouldSkipDuplicateCheck(config.url || '')) {
          const key = getRequestKey(config);
          pendingMap.delete(key);
        }
        // 文件流
        if (res.config.responseType === "blob" && res.status === 200) {
          return res;
        }
        if (res.data.code !== 200 || (res.data.data && res.data.data.code && res.data.data.code !== 200)) {
          // 配置是否显示报错信息
          if (!res.config.headers.hideError) {
            message.error(res.data.data && res.data.data.msg ? res.data.data.msg : res.data.message);
          }
          if (res.data.code === 401) {
            // 遍历map请求队列取消请求后删除
            pendingMap.forEach((value, key) => {
              value.controller.abort();
              pendingMap.delete(key);
            });
            UserStore.logout();
            router.replace({ name: "Login" });
          }
        }
        return res.data;
      },
      (err: any) => {
        if (axios.isCancel(err)) {
          return new Promise(() => {
            console.log("取消请求",err);
          });
        } else {
          message.error(err.response.data.message);
          return err.response.data;
        }
      }
    );
  }
  public request(config: AxiosRequestConfig): Promise<IResponse> {
    return this.instance.request(config);
  }
  public get(url: string, config?: AxiosRequestConfig): Promise<IResponse> {
    return this.instance.get(url, config);
  }
  public post(url: string, data?: { [arg: string]: any }, config?: AxiosRequestConfig): Promise<IResponse> {
    return this.instance.post(url, data, config);
  }
  public put(url: string, data?: { [arg: string]: any }, config?: AxiosRequestConfig): Promise<IResponse> {
    return this.instance.put(url, data, config);
  }
  public delete(url: string, config?: AxiosRequestConfig): Promise<IResponse> {
    return this.instance.delete(url, config);
  }
  public cancelRequest(requestId: string) {
    const request = pendingMap.get(requestId);
    if (request) {
      request.controller.abort();
      pendingMap.delete(requestId);
    }
  }
}
// 登录时效判断
const updateTimeHandle = async () => {
  if (!UserStore) return;
  // 超过时间清除，小于30分钟更新
  const timeDiff = new Date(UserStore.userInfo.expiresAt).getTime() - new Date().getTime();
  if (timeDiff < 1000 * 60 * 30 && timeDiff > 0) {
    await axios
      .get("/api/user/refresh-authorization", {
        headers: {
          Authorization: "Bearer " + UserStore.userInfo.authorization,
        },
      })
      .then((res) => {
        UserStore.userInfo.authorization = res.data.authorization;
        UserStore.userInfo.expiresAt = res.data.expiresAt;
      });
  } else if (timeDiff < 0) {
    UserStore.logout();
    router.replace({ name: "Login" });
  }
};
export default Request;

// 添加不需要重复请求检查的接口
export const addNoDuplicateCheckUrl = (url: string | RegExp) => {
  NO_DUPLICATE_CHECK_URLS.push(url);
};

// 移除不需要重复请求检查的接口
export const removeNoDuplicateCheckUrl = (url: string | RegExp) => {
  const index = NO_DUPLICATE_CHECK_URLS.indexOf(url);
  if (index > -1) {
    NO_DUPLICATE_CHECK_URLS.splice(index, 1);
  }
};

// 获取当前白名单列表
export const getNoDuplicateCheckUrls = () => {
  return [...NO_DUPLICATE_CHECK_URLS];
};

// 创建第二个服务的请求实例
export const createSecondaryRequest = () => {
  return new Request({
    baseURL: "/fff/",
    timeout: 5 * 60 * 1000,
    repeatRequest: false,
  });
};
